/**
 * ProductDetailPage.jsx
 * Route: /product/:sku
 * Price display removed — all enquiries via WhatsApp.
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronLeft, ChevronRight, ShoppingBag, MessageCircle,
  Share2, ArrowLeft, CheckCircle, Shield, Star, ZoomIn,
} from 'lucide-react';
import api from '../utils/api';
import { addToCart } from '../utils/cart';
import { toast } from 'sonner';
import { normalizeImages } from '../utils/imageUtils'; // ✅ HEIC fix

const Stars = ({ n = 5 }) => (
  <div className="flex gap-0.5">
    {[...Array(5)].map((_, i) => (
      <Star key={i} className={`w-3.5 h-3.5 ${i < n ? 'fill-[#E5A93C] text-[#E5A93C]' : 'fill-gray-200 text-gray-200'}`} />
    ))}
  </div>
);

const WA_NUMBER = '919538769669';

const ProductDetailPage = () => {
  const { sku } = useParams();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);
  const [addedToCart, setAddedToCart] = useState(false);
  const [lightbox, setLightbox] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    fetchProduct();
  }, [sku]);

  const fetchProduct = async () => {
    setLoading(true);
    setActiveImg(0);
    try {
      const [prodRes, relRes] = await Promise.all([
        api.get(`/products/${sku}`),
        api.get(`/products/${sku}/related?limit=4`).catch(() => ({ data: [] })),
      ]);
      setProduct(prodRes.data);
      setRelated(relRes.data);
    } catch (err) {
      console.error(err);
      toast.error('Product not found');
      navigate('/collections');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = () => {
    addToCart(product);
    window.dispatchEvent(new Event('cartUpdated'));
    toast.success('Added to cart!');
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2500);
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      await navigator.share({ title: product.title, url });
    } else {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-[#E5A93C] border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-gray-500">Loading product…</p>
        </div>
      </div>
    );
  }

  if (!product) return null;

  // ✅ normalizeImages converts HEIC URLs → f_auto (JPEG/WebP) for all browsers
  const images = normalizeImages(product.images);

  const waText = `Hi, I'm interested in ${product.category} design number ${product.sku} — ${product.title}. I'd like to know the best price. (AHMEDABAD NX)`;
  const whatsappUrl = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(waText)}`;

  const breadcrumbs = [
    { label: 'Home', path: '/' },
    { label: 'Collections', path: '/collections' },
    { label: product.category, path: `/collections/${product.category.toLowerCase().replace(/\s+/g, '-')}` },
    { label: product.title, path: null },
  ];

  return (
    <div className="min-h-screen bg-[#FFFDF9]">

      {/* Breadcrumb */}
      <div className="border-b border-gray-100 bg-white">
        <div className="container mx-auto px-4 md:px-8 py-3 flex items-center gap-2 text-xs text-gray-400 flex-wrap">
          <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-[#E5A93C] hover:text-[#2A0000] transition-colors mr-1">
            <ArrowLeft className="w-3.5 h-3.5" />
            <span>Back</span>
          </button>
          {breadcrumbs.map((crumb, i) => (
            <React.Fragment key={i}>
              {i > 0 && <span className="text-gray-300">/</span>}
              {crumb.path ? (
                <Link to={crumb.path} className="hover:text-[#2A0000] transition-colors truncate max-w-[120px]">{crumb.label}</Link>
              ) : (
                <span className="text-[#2A0000] font-medium truncate max-w-[180px]">{crumb.label}</span>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Main Product Area */}
      <div className="container mx-auto px-4 md:px-8 py-10 md:py-16">
        <div className="grid md:grid-cols-2 gap-10 lg:gap-16 items-start">

          {/* Left: Image Gallery */}
          <div className="space-y-3">
            <div
              className="relative overflow-hidden rounded-sm bg-[#F7F2EE] cursor-zoom-in"
              style={{ paddingBottom: '125%' }}
              onClick={() => setLightbox(true)}
            >
              <AnimatePresence mode="wait">
                <motion.img
                  key={activeImg}
                  src={images[activeImg]}
                  alt={product.title}
                  initial={{ opacity: 0, scale: 1.04 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4 }}
                  className="absolute inset-0 w-full h-full object-cover"
                  onError={(e) => { e.target.src = 'https://images.pexels.com/photos/10317127/pexels-photo-10317127.jpeg'; }}
                />
              </AnimatePresence>
              <div className="absolute bottom-3 right-3 w-8 h-8 rounded-full bg-white/80 backdrop-blur-sm flex items-center justify-center shadow">
                <ZoomIn className="w-4 h-4 text-[#2A0000]" />
              </div>
              {images.length > 1 && (
                <>
                  <button onClick={(e) => { e.stopPropagation(); setActiveImg((p) => (p - 1 + images.length) % images.length); }}
                    className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/80 flex items-center justify-center shadow hover:bg-white">
                    <ChevronLeft className="w-4 h-4 text-[#2A0000]" />
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); setActiveImg((p) => (p + 1) % images.length); }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/80 flex items-center justify-center shadow hover:bg-white">
                    <ChevronRight className="w-4 h-4 text-[#2A0000]" />
                  </button>
                </>
              )}
            </div>
            {images.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
                {images.map((img, i) => (
                  <button key={i} onClick={() => setActiveImg(i)}
                    className={`flex-shrink-0 w-16 h-20 rounded-sm overflow-hidden border-2 transition-all ${i === activeImg ? 'border-[#E5A93C]' : 'border-transparent opacity-60 hover:opacity-90'}`}>
                    <img
                      src={img}
                      alt=""
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.src = 'https://images.pexels.com/photos/10317127/pexels-photo-10317127.jpeg'; }}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Right: Product Info */}
          <div className="space-y-6">
            {/* Badges */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] font-bold tracking-widest uppercase px-2.5 py-1 rounded-sm"
                style={{ background: '#2A0000', color: '#E5A93C' }}>
                {product.category}
              </span>
              {product.subcategory && (
                <span className="text-[10px] font-medium tracking-wider uppercase px-2.5 py-1 rounded-sm border border-[#E5A93C]/40 text-[#E5A93C]">
                  {product.subcategory}
                </span>
              )}
              {product.featured && (
                <span className="text-[10px] font-bold tracking-wider uppercase px-2.5 py-1 rounded-sm bg-amber-50 text-amber-700">
                  ★ Featured
                </span>
              )}
            </div>

            {/* Title */}
            <div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-[#2A0000] leading-tight mb-1">
                {product.title}
              </h1>
              <p className="text-xs font-mono text-gray-400 tracking-widest uppercase">{product.sku}</p>
            </div>

            <div className="flex items-center gap-2">
              <Stars n={5} />
              <span className="text-xs text-gray-400">New Arrival</span>
            </div>

            {/* Color */}
            {product.color && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-gray-500 mb-2">Colour</p>
                <div className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full border border-gray-300 shadow-sm"
                    style={{ background: product.color.toLowerCase().replace(/\s+/g, '') }} />
                  <span className="text-sm text-[#2C2C2C]">{product.color}</span>
                </div>
              </div>
            )}

            {/* Description */}
            {product.description && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-gray-500 mb-2">Description</p>
                <p className="text-sm text-gray-600 leading-relaxed">{product.description}</p>
              </div>
            )}

            {/* CTA Buttons */}
            <div className="space-y-3 pt-2">
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2.5 w-full py-3.5 rounded-sm font-semibold text-sm transition-all duration-200 hover:scale-[1.01] active:scale-[0.99]"
                style={{ background: '#25D366', color: '#fff' }}
                data-testid="product-detail-whatsapp"
              >
                <MessageCircle className="w-5 h-5" />
                Get Best Price on WhatsApp
              </a>

              <button
                onClick={handleAddToCart}
                className="flex items-center justify-center gap-2.5 w-full py-3.5 rounded-sm font-semibold text-sm border-2 transition-all duration-200 hover:scale-[1.01] active:scale-[0.99]"
                style={{
                  borderColor: '#2A0000',
                  background: addedToCart ? '#2A0000' : 'transparent',
                  color: addedToCart ? '#E5A93C' : '#2A0000',
                }}
                data-testid="product-detail-add-to-cart"
              >
                <ShoppingBag className="w-5 h-5" />
                {addedToCart ? 'Added to Cart ✓' : 'Add to Cart'}
              </button>

              <div className="flex gap-2">
                <button onClick={handleShare}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-sm border border-gray-200 text-sm text-gray-500 hover:border-gray-300 transition-all">
                  <Share2 className="w-4 h-4" />
                  Share
                </button>
              </div>
            </div>

            {/* Trust badges */}
            <div className="grid grid-cols-2 gap-3 pt-2 border-t border-gray-100">
              {[
                { Icon: CheckCircle, label: 'Authentic Fabric' },
                { Icon: Shield, label: 'Trusted Since 2014' },
              ].map(({ Icon, label }) => (
                <div key={label} className="flex flex-col items-center gap-1.5 p-3 bg-[#F7F2EE] rounded-sm text-center">
                  <Icon className="w-4 h-4 text-[#E5A93C]" />
                  <span className="text-[10px] text-gray-500 leading-tight">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Related Products */}
      {related.length > 0 && (
        <section className="py-12 md:py-16 bg-white border-t border-gray-100">
          <div className="container mx-auto px-4 md:px-8">
            <div className="flex items-center justify-between mb-8">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-[#2A0000]">You May Also Like</h2>
              <Link to={`/collections/${product.category.toLowerCase().replace(/\s+/g, '-')}`} className="text-sm text-[#E5A93C] hover:underline">
                View all →
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {related.map((rel) => <RelatedCard key={rel.sku} product={rel} />)}
            </div>
          </div>
        </section>
      )}

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.93)' }}
            onClick={() => setLightbox(false)}
          >
            <motion.img
              initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }}
              src={images[activeImg]} alt={product.title}
              className="max-w-full max-h-full object-contain rounded-sm"
              onClick={(e) => e.stopPropagation()}
              onError={(e) => { e.target.src = 'https://images.pexels.com/photos/10317127/pexels-photo-10317127.jpeg'; }}
            />
            <button className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20" onClick={() => setLightbox(false)}>✕</button>
            {images.length > 1 && (
              <>
                <button onClick={(e) => { e.stopPropagation(); setActiveImg((p) => (p - 1 + images.length) % images.length); }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20">
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button onClick={(e) => { e.stopPropagation(); setActiveImg((p) => (p + 1) % images.length); }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20">
                  <ChevronRight className="w-5 h-5" />
                </button>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const RelatedCard = ({ product }) => {
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(false);
  // ✅ normalizeImages handles HEIC for related cards too
  const images = normalizeImages(product.images);

  return (
    <div className="group cursor-pointer" onClick={() => navigate(`/product/${product.sku}`)}
      onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
      <div className="relative overflow-hidden rounded-sm bg-[#F7F2EE] mb-3" style={{ paddingBottom: '130%' }}>
        <img
          src={hovered && images[1] ? images[1] : images[0]}
          alt={product.title}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          onError={(e) => { e.target.src = 'https://images.pexels.com/photos/10317127/pexels-photo-10317127.jpeg'; }}
        />
      </div>
      <p className="text-[10px] font-mono tracking-widest text-gray-400 mb-0.5">{product.sku}</p>
      <h4 className="text-sm font-semibold text-[#2C2C2C] line-clamp-2 group-hover:text-[#2A0000] transition-colors">{product.title}</h4>
    </div>
  );
};

export default ProductDetailPage;